import { AlertTriangle, CalendarClock, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/client/components/ui/button";
import {
  type MaintenanceWindow,
  maintenanceApi,
} from "@/client/features/maintenance/maintenance-api";
import { formatDateTime, formatRelativeTime } from "@/client/lib/format";
import { cn } from "@/client/lib/utils";

const DISMISS_PREFIX = "maint-dismissed:";

/** Dismissal is keyed by window + updatedAt, so an edit re-shows the banner. */
function dismissKey(w: MaintenanceWindow): string {
  return `${DISMISS_PREFIX}${w.id}:${w.updatedAt}`;
}

function isActive(w: MaintenanceWindow, now: number): boolean {
  // An explicit "in progress" status always wins. Otherwise a window is only
  // "active" when we can bound it: now within [start, end]. Open-ended windows
  // (no end) are NOT inferred active from a guessed duration - they rely on
  // staff flipping the status, so the banner never falsely claims "in progress".
  if (w.status === "in_progress") {
    return true;
  }
  if (!w.endsAt) {
    return false;
  }
  const start = new Date(w.startsAt).getTime();
  const end = new Date(w.endsAt).getTime();
  return now >= start && now <= end;
}

/** Windows worth surfacing: active now, or scheduled and starting within 14 days. */
function isRelevant(w: MaintenanceWindow, now: number): boolean {
  if (w.status === "cancelled" || w.status === "completed") {
    return false;
  }
  if (isActive(w, now)) {
    return true;
  }
  const start = new Date(w.startsAt).getTime();
  const horizon = now + 14 * 24 * 60 * 60 * 1000;
  return start > now && start <= horizon;
}

function formatRange(w: MaintenanceWindow): string {
  const start = formatDateTime(w.startsAt);
  if (!w.endsAt) {
    return `${start} (end TBD)`;
  }
  return `${start} - ${formatDateTime(w.endsAt)}`;
}

function BannerRow({ w, onDismiss }: { w: MaintenanceWindow; onDismiss: () => void }) {
  const now = Date.now();
  const active = isActive(w, now);
  const emphasized = active || w.kind === "emergency";
  // Active or emergency windows can't be dismissed - they're too important to hide.
  const dismissible = !emphasized;

  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
        emphasized
          ? "border-[var(--warning-solid)] bg-[var(--warning-bg)] text-[var(--warning-fg)]"
          : "border-border bg-muted/40 text-foreground",
      )}
    >
      {emphasized ? (
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      ) : (
        <CalendarClock className="text-muted-foreground mt-0.5 size-4 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {active ? "Maintenance in progress: " : "Upcoming maintenance: "}
          {w.title}
        </p>
        <p className={cn("text-xs", emphasized ? "opacity-80" : "text-muted-foreground")}>
          {active ? formatRange(w) : `Starts ${formatRelativeTime(w.startsAt)} · ${formatRange(w)}`}{" "}
          &middot;{" "}
          <Link to="/calendar" className="underline underline-offset-2">
            View details
          </Link>
        </p>
      </div>
      {dismissible ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <X className="size-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Top-of-portal banner for active/imminent maintenance. Pulls from the same
 * source as the calendar; non-critical entries are dismissible (remembered in
 * localStorage until the window is edited). Renders nothing when there's nothing
 * relevant or the fetch fails - it must never block the page.
 */
export function MaintenanceBanner() {
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await maintenanceApi.list();
        if (active) setWindows(res.windows);
      } catch {
        // Silent: a banner failure must not disrupt the page.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const visible = useMemo(() => {
    const now = Date.now();
    return windows
      .filter((w) => isRelevant(w, now))
      .filter((w) => !dismissed.has(dismissKey(w)))
      .filter((w) => {
        try {
          return localStorage.getItem(dismissKey(w)) === null;
        } catch {
          return true;
        }
      })
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  }, [windows, dismissed]);

  if (visible.length === 0) {
    return null;
  }

  const dismiss = (w: MaintenanceWindow) => {
    try {
      localStorage.setItem(dismissKey(w), "1");
    } catch {
      // Ignore storage failures; dismissal just won't persist across reloads.
    }
    setDismissed((prev) => new Set(prev).add(dismissKey(w)));
  };

  return (
    <div className="mb-4 flex flex-col gap-2">
      {visible.map((w) => (
        <BannerRow key={w.id} w={w} onDismiss={() => dismiss(w)} />
      ))}
    </div>
  );
}
